import { useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { ActivityIndicator, Divider, Text } from "react-native-paper";
import { Image, Platform, ScrollView, TouchableOpacity, View, useWindowDimensions } from "react-native";
import { cond, stubTrue } from "lodash";
import { Button } from "../../components";
import { Chip as ChipStyled } from "../../components/styled";
import { formatCurrency } from "../utils";
import { convertToTimezone, readableDate } from "../../utils/dateUtils";
import {
  EVENT_DETAILS_FRAGMENT,
  ORDER_PREVIEW_FRAGMENT,
  OFFLINE_FEE_FRAGMENT,
  EVENT_ELIGIBILITY_QUERY,
  EVENT_REGISTRATION_QUERY,
  CREATE_EVENT_WAITLIST_ENTRY_MUTATION,
  EVENT_WAITLIST_ENTRY_QUERY,
} from "./queries";
import {
  CreateEventWaitlistEntryMutation,
  EventDetailsQuery,
  EventEligibilityQuery,
  EventWaitlistEntryQuery,
  EventDetailsCardProps,
  RegistrationStatus,
} from "./types";
import CheckSquareIcon from "../../svgs/CheckSquareIcon";
import { LinearGradient } from "expo-linear-gradient";
import { afterRegistrationClose, beforeRegistrationOpen, formatEventDate, isCancelled } from "./helpers";
import HTMLDisplay from "../../components/HTMLDisplay";
import { colors } from "../../../theme";
import { ScreenProps } from "../types";
import useSnackBar from "../../hooks/useSnackBar";
import { CURRENT_MEMBER_FRAGMENT } from "../fragments";
import { Feather } from "@expo/vector-icons";
import { WaitlistModal, EligibilityModal } from "./modals";
import LocationSection from "./LocationSection";
import NotificationBanner from "../../components/NotificationBanner";
import EventDateSection from "./EventDateSection";

const fullBleedWidth = 1470;
const bannerImage = require("../../../assets/event-details-banner.jpg");

export const EVENT_DETAILS_QUERY = gql`
  query EventDetailsQuery($slug: String!) {
    event(slug: $slug) {
      ...EventDetailsFragment
      ...OrderPreviewFragment
      ...OfflineFeeFragment
    }
    currentMember {
      ...CurrentMemberFragment
    }
  }
  ${EVENT_DETAILS_FRAGMENT}
  ${ORDER_PREVIEW_FRAGMENT}
  ${OFFLINE_FEE_FRAGMENT}
  ${CURRENT_MEMBER_FRAGMENT}
`;

const EventDetailsBanner = ({ event }: { event: EventDetailsQuery["event"] }) => {
  const { width } = useWindowDimensions();
  return (
    <View className={`w-full overflow-hidden ${width > fullBleedWidth ? "rounded-lg" : ""}`}>
      <View className="relative flex justify-center items-start">
        <Image source={{ uri: event?.bannerImageUrl || bannerImage }} className="w-full h-[350px]" />
        <LinearGradient
          colors={["rgba(0, 0, 0, 0.55)", "rgba(76, 76, 76, 0)"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          locations={[0.099, 0.9721]}
          style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
        />
        <View className="absolute mt-[-50px] lg:mt-0 px-8 lg:px-10">
          <Text className="text-title-2 md:text-[36px] xl:text-title-1 text-white font-700">{event?.title}</Text>
          <Text className="text-regular-normal md:text-title-5 xl:text-title-4 text-white font-700 mt-2 md:mt-4">
            {formatEventDate(event?.startsAt, event?.endsAt, event?.timezone)}
          </Text>
        </View>
      </View>
    </View>
  );
};

export const EventDetailsCard = ({
  event,
  setEligibilityModalVisible,
  registrationEligible,
  registered,
  onRegistrationPress,
  onTicketPress,
  email,
  waitlistEntry,
  onWaitlistPress,
  waitlistEntryLoading,
}: EventDetailsCardProps) => {
  const [waitlistModalVisible, setWaitlistModalVisible] = useState<boolean>(false);
  const showSnackBar = useSnackBar();

  const handleWaitlistPress = async () => {
    const success = await onWaitlistPress(event.id);
    if (success) {
      setWaitlistModalVisible(true);
    } else {
      showSnackBar({
        message: "An error occurred, failed to join waitlist.",
        duration: 5000,
        icon: "alert-triangle",
        style: { backgroundColor: colors.red.dark },
      });
    }
  };

  const RegisterStatus = cond([
    [
      isCancelled,
      () => (
        <ChipStyled className="rounded-2xl bg-yellow-lightest" textStyle="my-0.5" testID="register-status">
          <Feather name="alert-triangle" color={colors.yellow.dark} size={16} />
          <Text className="text-yellow-dark font-400 ml-2">Event Canceled</Text>
        </ChipStyled>
      ),
    ],
    [
      () => registered,
      () => (
        <ChipStyled className="rounded-2xl bg-green-lightest" textStyle="my-0.5" testID="register-status">
          <Text className="text-ink font-400">Already Registered</Text>
        </ChipStyled>
      ),
    ],
    [
      beforeRegistrationOpen,
      () => (
        <ChipStyled className="rounded-2xl bg-sky-light" textStyle="my-0.5" testID="register-status">
          <Text className="text-ink font-400">Registration Opening Soon</Text>
        </ChipStyled>
      ),
    ],
    [
      afterRegistrationClose,
      () => (
        <ChipStyled className="rounded-2xl bg-sky-light" textStyle="my-0.5" testID="register-status">
          <Text className="text-ink font-400">Registration Closed</Text>
        </ChipStyled>
      ),
    ],
    [
      () => !!event.registrationFull,
      () => (
        <ChipStyled className="rounded-2xl bg-yellow-lightest" textStyle="my-0.5" testID="register-status">
          <Text className="text-yellow-dark font-400">Registration Full</Text>
        </ChipStyled>
      ),
    ],
    [
      () => !registrationEligible,
      () => (
        <ChipStyled className="rounded-2xl bg-sky-light" textStyle="my-0.5" testID="register-status">
          <Text className="text-ink font-400">Registration Unavailable</Text>
        </ChipStyled>
      ),
    ],
    [
      stubTrue,
      () => (
        <ChipStyled className="rounded-2xl bg-green-lightest" textStyle="my-0.5" testID="register-status">
          <Text className="text-green-dark font-500">Registration Open</Text>
        </ChipStyled>
      ),
    ],
  ]);

  const RegisterButton = cond([
    [
      () => registered,
      () => (
        <Button mode="contained" testID="register-button" onPress={onTicketPress}>
          View Registration
        </Button>
      ),
    ],
    [
      beforeRegistrationOpen,
      (event) => (
        <Button mode="contained" disabled testID="register-button">
          <View>
            <View>Registration Opens </View>
            <View>{readableDate(convertToTimezone(event.registrationOpenAt, event.timezone))}</View>
          </View>
        </Button>
      ),
    ],
    [
      afterRegistrationClose,
      () => (
        <Button mode="contained" disabled testID="register-button">
          Registration Closed
        </Button>
      ),
    ],
    [
      () => registrationEligible && (!event.registrationFull || !!waitlistEntry?.promoted),
      () => (
        <Button mode="contained" testID="register-button" onPress={onRegistrationPress}>
          Register
        </Button>
      ),
    ],
    [
      () => !!waitlistEntry,
      () => (
        <View>
          <Text className="text-small-normal text-center font-400 mb-3">
            Event is full, sign up to be notified if registration becomes available.
          </Text>
          <Button mode="secondary" testID="register-button" icon="check" iconPosition="right">
            You will be notified
          </Button>
          <WaitlistModal {...{ waitlistModalVisible, setWaitlistModalVisible, email }} />
        </View>
      ),
    ],
    [
      () => !!event.waitlistEnabled,
      () => (
        <View>
          <Text className="text-small-normal text-center font-400 mb-3">
            Event is full, sign up to be notified if registration becomes available.
          </Text>
          <Button mode="contained" testID="register-button" onPress={handleWaitlistPress} disabled={waitlistEntryLoading}>
            <View className="flex flex-row justify-center items-center">
              <Text className="text-regular-tight font-500 text-sky-white">Notify Me</Text>
              {waitlistEntryLoading && <ActivityIndicator color={colors.sky.white} style={{ marginLeft: 15 }} />}
            </View>
          </Button>
          <WaitlistModal {...{ waitlistModalVisible, setWaitlistModalVisible, email }} />
        </View>
      ),
    ],
    [
      () => !!event.registrationFull,
      () => (
        <View>
          <Button mode="contained" disabled testID="register-button">
            Registration Full
          </Button>
          <Text className="text-small-normal text-center mt-3 font-500" testID="ineligible-text">
            Event has reached maximum capacity
          </Text>
        </View>
      ),
    ],
    [
      () => !registrationEligible,
      () => (
        <View>
          <Button mode="contained" testID="register-button" onPress={() => setEligibilityModalVisible(true)} isValid={false}>
            Registration Unavailable
          </Button>
          <Text className="text-small-normal text-center mt-3 font-500" testID="ineligible-text">
            <TouchableOpacity onPress={() => setEligibilityModalVisible(true)} testID="ineligible-link">
              <Text className="text-primary font-500 underline">Why can't I register?</Text>
            </TouchableOpacity>
          </Text>
        </View>
      ),
    ],
    [
      stubTrue,
      () => (
        <Button mode="contained" testID="register-button" onPress={onRegistrationPress}>
          Register
        </Button>
      ),
    ],
  ]);

  const orderPreview = event?.orderPreview ?? null;
  const offlineFee = event?.offlineFee ?? null;
  const offlinePrice = Number(offlineFee?.priceCents) || 0;
  const hasFees = orderPreview || offlineFee;
  const hasLocationInfo = event?.facility || event?.address1 || event?.location;

  return (
    <View
      className="py-6 px-1 w-full lg:w-[320px] lg:min-w-[320px] xl:w-[367px] xl:min-w-[367px] shadow-md rounded-lg bg-white border-sky"
      testID="event-details"
    >
      <View className="px-6">
        <View className="mt-2 mb-3">
          <View className="flex-row items-center justify-start">{RegisterStatus(event)}</View>
        </View>
        <View className="divide-y divide-sky-light">
          <EventDateSection event={event} />
          {hasLocationInfo && (
            <View className="flex flex-row py-3">
              <LocationSection event={event} />
            </View>
          )}
          <View className="flex flex-row py-3">
            <CheckSquareIcon height={22} width={22} />
            <View className="flex-1 min-w-0 pl-2">
              <Text className="text-small-normal font-700 text-ink-darker">Registration Info</Text>
              <View className="flex-row flex-wrap">
                {beforeRegistrationOpen(event) && (
                  <Text
                    className="text-small-normal text-ink-darker font-400"
                    testID="registration-open"
                  >{`Opens: ${readableDate(convertToTimezone(event?.registrationOpenAt, event.timezone))}`}</Text>
                )}
                <Text
                  className="text-small-normal text-ink-darker font-400"
                  testID="registration-close"
                >{`Closes: ${readableDate(convertToTimezone(event?.registrationCloseAt, event.timezone))}`}</Text>
              </View>
            </View>
          </View>
          {hasFees && (
            <View className="flex flex-row py-3">
              <Feather name="dollar-sign" size={22} />
              <View className="flex-1 min-w-0 pl-2">
                <Text className="text-small-normal font-700 text-ink-darker">Fees</Text>
                {orderPreview && orderPreview?.lineItems?.length > 0 && (
                  <View className="flex-col flex-wrap">
                    {orderPreview?.lineItems.map((lineItem, idx) => (
                      <View key={idx} className="flex-col">
                        <Text className="text-small-normal text-ink-darker font-400 flex-wrap" testID="order-line-item">
                          {formatCurrency({ value: lineItem.priceCents / 100, unit: "usd", minimumFractionDigits: 0 })}{" "}
                          {lineItem.name}
                        </Text>
                      </View>
                    ))}
                  </View>
                )}
                {offlineFee && (
                  <Text className="text-small-normal text-ink-darker font-400 flex-wrap">
                    {formatCurrency({ value: offlinePrice / 100, unit: "usd", minimumFractionDigits: 0 })} Course Fees
                  </Text>
                )}
              </View>
            </View>
          )}
        </View>
        {!isCancelled(event) && <View className="mt-3">{RegisterButton(event)}</View>}
      </View>
    </View>
  );
};

export const EventDetailsScreen = ({ navigation, route }: ScreenProps<"EventDetails">) => {
  const { data, loading } = useQuery<EventDetailsQuery>(EVENT_DETAILS_QUERY, {
    variables: { slug: route.params.slug },
  });
  const { data: eligibilityData } = useQuery<EventEligibilityQuery>(EVENT_ELIGIBILITY_QUERY, {
    variables: { slug: route.params.slug },
  });
  const { data: registrationData } = useQuery(EVENT_REGISTRATION_QUERY, {
    variables: { slug: route.params.slug, status: RegistrationStatus.REGISTERED },
  });
  const {
    data: waitlistEntryData,
    refetch: refetchWaitlistEntry,
    loading: waitlistEntryLoading,
  } = useQuery<EventWaitlistEntryQuery>(EVENT_WAITLIST_ENTRY_QUERY, {
    variables: { slug: route.params.slug },
  });

  const [waitlistMutation] = useMutation<CreateEventWaitlistEntryMutation>(CREATE_EVENT_WAITLIST_ENTRY_MUTATION);

  const onWaitlistPress = async (eventId: string): Promise<boolean> => {
    const { data: createEventWaitlistEntryResponse } = await waitlistMutation({ variables: { eventId } });
    const { success } = createEventWaitlistEntryResponse?.createEventWaitlistEntry || {};

    if (success) {
      await refetchWaitlistEntry();
    }

    return !!success;
  };
  const { width } = useWindowDimensions();
  const [eligibilityModalVisible, setEligibilityModalVisible] = useState<boolean>(false);
  const registrationEligible = !!eligibilityData?.eventEligibility?.isEligible;
  const eligibilityErrors = eligibilityData?.eventEligibility?.eligibilityErrors || [];
  const registered = !!registrationData?.eventRegistration?.registeredAt;

  if (loading) {
    return <ActivityIndicator className="flex-1 items-center justify-center" testID="query-loader" />;
  }
  const { event } = data || {};
  const email = data?.currentMember?.primaryEmail || "";

  const onRegistrationPress = () => navigation.navigate("EventRegistration", { slug: route.params.slug });
  const onTicketPress = () => navigation.navigate("EventConfirmation", { slug: route.params.slug });

  const children = event && (
    <View className={`relative sm:w-full lg:max-w-[1200px] md:mx-auto ${width > fullBleedWidth ? "mt-6" : ""}`}>
      <View className="w-full">
        <EventDetailsBanner event={event} />
      </View>
      <View className="w-full flex flex-col flex-col-reverse lg:flex-row lg:justify-between px-8 lg:px-10">
        <View className="mt-9 w-full lg:w-1/2 xl:w-7/12">
          {isCancelled(event) && (
            <NotificationBanner
              backgroundColor={colors.yellow.lightest}
              borderColor="border-yellow"
              IconComp={() => <Feather name="alert-triangle" color={colors.yellow.dark} size={16} />}
              text="Event has been canceled."
              textColor="text-yellow-dark"
              iconColor={colors.yellow.dark}
            />
          )}
          <Text className="text-title-4 font-700 md:mt-8 mb-4">Event Details</Text>
          <HTMLDisplay html={event?.description} fontSize={16} lineHeight={24} color={colors.ink.dark} />
          {event?.hasWaitlist && (
            <View testID="waitlist">
              <Divider className="border-[1px] border-sky-lighter mb-8 mt-6" />
              <Text className="text-title-4 font-700 mb-6">Waitlist Policy</Text>
              <Text className="text-regular-normal text-ink-dark leading-7 font-400">
                If a PAT becomes full before the deadline date, interested participants can sign up to be notified in the event
                a spot opens up. Should an event open more spots, anyone who requested a notification would be alerted, and
                those open spots would fill on a first come, first served basis. Any questions about your status can be directed
                to the Section Office hosting the event.
              </Text>
            </View>
          )}
        </View>
        <View className={"justify-center items-center mt-[-60px] max-h-[438px]"}>
          <EventDetailsCard
            event={event}
            setEligibilityModalVisible={setEligibilityModalVisible}
            registrationEligible={registrationEligible}
            registered={registered}
            onRegistrationPress={onRegistrationPress}
            onTicketPress={onTicketPress}
            email={email}
            waitlistEntry={waitlistEntryData?.eventWaitlistEntry}
            onWaitlistPress={onWaitlistPress}
            waitlistEntryLoading={waitlistEntryLoading}
          />
        </View>
        {eligibilityModalVisible && (
          <EligibilityModal {...{ eligibilityModalVisible, setEligibilityModalVisible, eligibilityErrors }} />
        )}
      </View>
    </View>
  );

  return (
    <View className="bg-white h-full min-h-full">{Platform.OS === "web" ? children : <ScrollView>{children}</ScrollView>}</View>
  );
};

export default EventDetailsScreen;
